import React, { useState } from 'react';
import { useLang } from '../lib/lang';
import { apiLogin, saveTokens, type AuthUser } from '../lib/auth';
import { QuickLoginPanel } from '../components/QuickLoginPanel';

// ── Verified 200-OK Unsplash photos ──────────────────────────────────────────
// Each tested via curl before use. onError fallback = styled gradient tile.
const IMG = {
  // Islamic/dome architecture — replaces the 404 mosque URL
  mosque:    'https://images.unsplash.com/photo-1583422409516-2895a77efded?auto=format&fit=crop&w=900&q=85',
  // Secondary architecture (golden dome, warm tones)
  arch:      'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=500&q=80',
  // Students in a classroom (Southeast Asian context)
  students:  'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&w=700&q=80',
  // Modern digital / government office
  digital:   'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=500&q=80',
  // School building exterior
  school:    'https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=500&q=80',
};

// Songket-inspired SVG tile (very subtle, scales infinitely)
const SONGKET = `data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48">
    <path d="M0 24L24 0L48 24L24 48Z" fill="none" stroke="rgba(247,208,0,0.06)" stroke-width="0.6"/>
    <circle cx="24" cy="24" r="1.5" fill="rgba(247,208,0,0.05)"/>
  </svg>`
)}`;

interface Props { onAuthenticated: (user: AuthUser) => void }

// ── Mosaic tile component ─────────────────────────────────────────────────────
interface TileProps {
  src: string; alt: string; caption?: string; captionSub?: string;
  style?: React.CSSProperties; fallbackGradient?: string;
}

function MosaicTile({ src, alt, caption, captionSub, style, fallbackGradient }: TileProps) {
  const [failed, setFailed] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: caption ? 'default' : 'default',
        ...style,
      }}
    >
      {!failed ? (
        <img
          src={src}
          alt={alt}
          onError={() => setFailed(true)}
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover', display: 'block',
            transition: 'transform 400ms ease, filter 400ms ease',
            transform: hovered ? 'scale(1.06)' : 'scale(1)',
            filter: hovered ? 'brightness(1.08)' : 'brightness(0.9)',
          }}
        />
      ) : (
        // CSS fallback — looks designed, not broken
        <div style={{
          width: '100%', height: '100%',
          background: fallbackGradient ?? 'linear-gradient(135deg, #1E3A5F 0%, #2D5F8F 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: '32px', opacity: 0.6 }}>🏛️</span>
        </div>
      )}

      {/* Base dark overlay — always present for text legibility */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(7,12,25,0.72) 0%, rgba(7,12,25,0.15) 55%, rgba(7,12,25,0.05) 100%)',
        transition: 'opacity 300ms ease',
        opacity: hovered ? 0.85 : 1,
        pointerEvents: 'none',
      }} />

      {/* Caption — slides up on hover */}
      {caption && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '14px 14px 12px',
          transform: hovered ? 'translateY(0)' : 'translateY(4px)',
          opacity: hovered ? 1 : 0.7,
          transition: 'all 300ms ease',
          pointerEvents: 'none',
        }}>
          <div style={{ fontFamily: '"Noto Serif", serif', fontSize: '13px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.3 }}>
            {caption}
          </div>
          {captionSub && (
            <div style={{ fontSize: '11px', color: '#F7D000', marginTop: '2px', fontWeight: 600 }}>
              {captionSub}
            </div>
          )}
        </div>
      )}

      {/* Hover shimmer — golden top-edge line that appears on hover */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, transparent, #F7D000, transparent)',
        opacity: hovered ? 1 : 0,
        transition: 'opacity 300ms ease',
        pointerEvents: 'none',
      }} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function LoginPage({ onAuthenticated }: Props) {
  const { lang, setLang } = useLang();
  const E = lang === 'en';

  const [email,         setEmail]         = useState('');
  const [password,      setPassword]      = useState('');
  const [error,         setError]         = useState<string | null>(null);
  const [loading,       setLoading]       = useState(false);
  const [pwVisible,     setPwVisible]     = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string | undefined>(undefined);

  function handleSelectRole(roleEmail: string, rolePassword: string) {
    setEmail(roleEmail);
    setPassword(rolePassword);
    setSelectedEmail(roleEmail);
    setError(null);
  }

  async function handleLogin(loginEmail: string, loginPassword: string) {
    setError(null);
    setLoading(true);
    try {
      const { pair, user } = await apiLogin(loginEmail, loginPassword);
      saveTokens(pair, user);
      onAuthenticated(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : (E ? 'Login failed. Please try again.' : 'Log masuk gagal. Sila cuba lagi.'));
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError(E ? 'Please enter your email and password.' : 'Sila masukkan emel dan kata laluan.');
      return;
    }
    void handleLogin(email.trim(), password);
  }

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { margin: 0; background: #070C19; font-family: 'Noto Sans', sans-serif; }

        /* ── Root split ─────────────────────────────────────────── */
        .lp-root { display: flex; min-height: 100vh; }

        /* ── LEFT PANEL ─────────────────────────────────────────── */
        .lp-left {
          width: 52%;
          min-height: 100vh;
          background:
            url('${SONGKET}'),
            radial-gradient(ellipse 90% 60% at 10% 20%, rgba(59,130,246,0.10) 0%, transparent 60%),
            radial-gradient(ellipse 70% 50% at 90% 80%, rgba(247,208,0,0.06) 0%, transparent 50%),
            linear-gradient(155deg, #060C1C 0%, #0B1529 40%, #0A1230 70%, #070D1E 100%);
          display: flex;
          flex-direction: column;
          padding: 40px 44px 36px 44px;
          position: relative;
          overflow: hidden;
        }

        /* Ambient glow orbs — positioned absolutely for depth */
        .lp-left::before {
          content: '';
          position: absolute;
          top: -80px; left: -80px;
          width: 340px; height: 340px;
          background: radial-gradient(circle, rgba(247,208,0,0.07) 0%, transparent 65%);
          pointer-events: none;
        }
        .lp-left::after {
          content: '';
          position: absolute;
          bottom: -100px; right: -60px;
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 60%);
          pointer-events: none;
        }

        /* ── RIGHT PANEL ─────────────────────────────────────────── */
        .lp-right {
          width: 48%;
          background: #FFFFFF;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 48px 56px;
          position: relative;
          overflow-y: auto;
          min-height: 100vh;
        }

        /* Subtle warm texture on right panel */
        .lp-right::before {
          content: '';
          position: absolute;
          top: 0; right: 0;
          width: 240px; height: 240px;
          background: radial-gradient(circle at 100% 0%, rgba(247,208,0,0.05) 0%, transparent 60%);
          pointer-events: none;
        }
        .lp-right::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 200px; height: 200px;
          background: radial-gradient(circle at 0% 100%, rgba(59,130,246,0.04) 0%, transparent 60%);
          pointer-events: none;
        }

        @media (max-width: 960px) {
          .lp-left  { display: none; }
          .lp-right { width: 100%; padding: 40px 28px; }
        }

        /* ── Form inputs ─────────────────────────────────────────── */
        .lp-field { position: relative; }
        .lp-input {
          width: 100%;
          background: #F8FAFC;
          border: 1.5px solid #E2E8F0;
          border-radius: 10px;
          color: #0F172A;
          padding: 13px 44px 13px 44px;
          font-size: 14px;
          font-family: 'Noto Sans', sans-serif;
          outline: none;
          transition: all 150ms ease;
          line-height: 1.4;
        }
        .lp-input::placeholder { color: #94A3B8; }
        .lp-input:hover:not(:focus):not(:disabled) {
          border-color: #CBD5E1;
          background: #FFFFFF;
        }
        .lp-input:focus {
          border-color: #F7D000;
          box-shadow: 0 0 0 3px rgba(247,208,0,0.14);
          background: #FFFFFF;
        }
        .lp-input.err { border-color: #EF4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.10); }
        .lp-input.pre-filled { border-color: #22C55E; background: #F0FDF4; box-shadow: 0 0 0 3px rgba(34,197,94,0.10); }
        .lp-input:disabled { opacity: 0.55; cursor: not-allowed; }
        .lp-input-icon {
          position: absolute;
          left: 14px;
          top: 50%; transform: translateY(-50%);
          color: #94A3B8;
          pointer-events: none;
          font-size: 16px;
          line-height: 1;
        }
        .lp-pw-toggle {
          position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: #94A3B8; padding: 4px; border-radius: 4px;
          transition: color 120ms; font-size: 15px;
          display: flex; align-items: center; justify-content: center;
        }
        .lp-pw-toggle:hover { color: #64748B; }

        /* ── Submit button ───────────────────────────────────────── */
        .lp-btn {
          width: 100%; height: 52px;
          background: linear-gradient(135deg, #F7D000 0%, #E8C200 100%);
          color: #0F172A;
          border: none; border-radius: 12px;
          font-size: 15px; font-weight: 700;
          font-family: 'Noto Sans', sans-serif;
          cursor: pointer;
          transition: all 150ms ease;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          box-shadow: 0 4px 14px rgba(247,208,0,0.25), 0 1px 3px rgba(0,0,0,0.08);
          letter-spacing: 0.01em;
        }
        .lp-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #FFDF00 0%, #F7D000 100%);
          box-shadow: 0 6px 20px rgba(247,208,0,0.35), 0 2px 6px rgba(0,0,0,0.08);
          transform: translateY(-1px);
        }
        .lp-btn:active:not(:disabled) {
          transform: translateY(0) scale(0.99);
          box-shadow: 0 2px 8px rgba(247,208,0,0.2);
        }
        .lp-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none; box-shadow: none; }

        /* ── Role cards ─────────────────────────────────────────── */
        .role-card {
          display: flex; align-items: center; gap: 12px;
          padding: 14px 16px;
          background: #F8FAFC;
          border: 1.5px solid #E2E8F0;
          border-radius: 12px;
          cursor: pointer;
          transition: all 150ms ease;
          text-align: left;
          font-family: 'Noto Sans', sans-serif;
          position: relative; overflow: hidden;
        }
        .role-card::before {
          content: '';
          position: absolute; top: 0; left: 0;
          width: 3px; height: 100%;
          background: #F7D000;
          transform: scaleY(0);
          transition: transform 150ms ease;
          transform-origin: center;
        }
        .role-card:hover {
          border-color: #F7D000;
          background: #FFFDF0;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(247,208,0,0.14), 0 2px 4px rgba(0,0,0,0.06);
        }
        .role-card:hover::before { transform: scaleY(1); }
        .role-card:active { transform: translateY(0); }
        .role-card:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }

        /* ── Lang toggle ─────────────────────────────────────────── */
        .lang-btn {
          padding: 5px 10px; background: none; border: none;
          font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; cursor: pointer;
          font-family: 'Noto Sans', sans-serif;
          transition: all 100ms ease; border-radius: 6px;
        }

        /* ── Stat chip on left panel ─────────────────────────────── */
        .stat-chip {
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          padding: 14px 18px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(247,208,0,0.18);
          border-radius: 12px;
          backdrop-filter: blur(8px);
          flex: 1;
          transition: all 200ms ease;
        }
        .stat-chip:hover {
          background: rgba(247,208,0,0.08);
          border-color: rgba(247,208,0,0.35);
          transform: translateY(-2px);
        }

        /* ── Divider ─────────────────────────────────────────────── */
        .lp-divider {
          display: flex; align-items: center; gap: 12px;
          margin: 22px 0;
        }
        .lp-divider-line { flex: 1; height: 1px; background: #E2E8F0; }
        .lp-divider-txt { font-size: 12px; color: #94A3B8; white-space: nowrap; }

        @keyframes lp-spin { to { transform: rotate(360deg); } }
        @keyframes lp-pulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
      `}</style>

      <div className="lp-root">

        {/* ════════════════════════════════════════════════════════
            LEFT PANEL — Brunei Government Identity
        ════════════════════════════════════════════════════════ */}
        <div className="lp-left">

          {/* ── Ministry branding ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '36px', position: 'relative', zIndex: 1 }}>
            <div style={{
              width: '46px', height: '46px',
              background: 'linear-gradient(135deg, #F7D000 0%, #C9A800 100%)',
              borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 16px rgba(247,208,0,0.30), 0 0 0 1px rgba(247,208,0,0.2)',
            }}>
              <span style={{ fontFamily: '"Noto Serif", serif', fontWeight: 800, fontSize: '17px', color: '#0F172A' }}>KPM</span>
            </div>
            <div>
              <div style={{ color: '#F1F5F9', fontFamily: '"Noto Serif", Georgia, serif', fontWeight: 700, fontSize: '13px', lineHeight: 1.3, letterSpacing: '-0.01em' }}>
                Kementerian Pendidikan
              </div>
              <div style={{ color: '#64748B', fontSize: '11px', marginTop: '1px', letterSpacing: '0.02em' }}>
                Negara Brunei Darussalam
              </div>
            </div>

            {/* Gold accent line */}
            <div style={{ marginLeft: 'auto', width: '32px', height: '2px', background: 'linear-gradient(90deg, #F7D000, transparent)', borderRadius: '2px' }} />
          </div>

          {/* ── Photo mosaic ── */}
          <div style={{ position: 'relative', zIndex: 1, flex: '0 0 auto' }}>

            {/* ROW 1 */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              {/* Large: Islamic architecture (mosque replacement) */}
              <MosaicTile
                src={IMG.mosque}
                alt="Seni bina Islam — Brunei Darussalam"
                caption="Brunei Darussalam"
                captionSub="Negara Beriman"
                fallbackGradient="linear-gradient(135deg, #0E2954 0%, #1A4080 50%, #0E3060 100%)"
                style={{ flex: '0 0 56%', height: '196px' }}
              />

              {/* Right column stacked */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* KPM logo tile — designed, not a photo */}
                <div style={{
                  height: '110px',
                  background: 'linear-gradient(135deg, #111827 0%, #1E2A3B 100%)',
                  borderRadius: '12px',
                  border: '1px solid rgba(247,208,0,0.25)',
                  borderTop: '3px solid #F7D000',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  gap: '8px', padding: '12px',
                  position: 'relative', overflow: 'hidden',
                }}>
                  {/* Background pattern */}
                  <div style={{ position: 'absolute', inset: 0, backgroundImage: `url('${SONGKET}')`, opacity: 0.4 }} />
                  <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <div style={{
                      width: '34px', height: '34px',
                      background: 'linear-gradient(135deg, #F7D000 0%, #C9A800 100%)',
                      borderRadius: '8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 6px',
                      boxShadow: '0 2px 10px rgba(247,208,0,0.3)',
                    }}>
                      <span style={{ fontFamily: '"Noto Serif", serif', fontWeight: 800, fontSize: '13px', color: '#0F172A' }}>KPM</span>
                    </div>
                    <div style={{ color: '#F1F5F9', fontSize: '9px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1.4 }}>
                      Kementerian<br />Pendidikan
                    </div>
                  </div>
                </div>

                {/* Architecture */}
                <MosaicTile
                  src={IMG.arch}
                  alt="Seni bina — Brunei"
                  fallbackGradient="linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)"
                  style={{ flex: 1, minHeight: '72px' }}
                />
              </div>
            </div>

            {/* ROW 2 */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <MosaicTile
                src={IMG.digital}
                alt="Transformasi Digital"
                fallbackGradient="linear-gradient(135deg, #0F2942 0%, #1A3F60 100%)"
                style={{ flex: '0 0 36%', height: '122px' }}
              />
              <MosaicTile
                src={IMG.students}
                alt="Pelajar Brunei Darussalam"
                caption="200+ Sekolah"
                captionSub="80,000+ Pelajar"
                fallbackGradient="linear-gradient(135deg, #064E3B 0%, #065F46 100%)"
                style={{ flex: 1, height: '122px' }}
              />
            </div>
          </div>

          {/* ── SERPS wordmark ── */}
          <div style={{ marginTop: '28px', position: 'relative', zIndex: 1 }}>
            <div style={{
              fontFamily: '"Noto Serif", Georgia, serif',
              fontSize: '38px', fontWeight: 700,
              background: 'linear-gradient(135deg, #F7D000 0%, #FFE566 40%, #C9A800 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1, letterSpacing: '-0.02em',
            }}>
              SERPS
            </div>
            <div style={{ color: '#475569', fontSize: '12px', marginTop: '5px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Sistem Pengurusan Rekod Sekolah
            </div>
          </div>

          {/* ── Stat chips ── */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', position: 'relative', zIndex: 1 }}>
            {[
              { n: '200+', label: E ? 'Schools' : 'Sekolah' },
              { n: '80K+', label: E ? 'Students' : 'Pelajar' },
              { n: '5K+',  label: E ? 'Educators' : 'Pendidik' },
            ].map(s => (
              <div key={s.label} className="stat-chip">
                <div style={{ color: '#F7D000', fontFamily: '"Noto Serif", serif', fontWeight: 700, fontSize: '18px', lineHeight: 1 }}>{s.n}</div>
                <div style={{ color: '#64748B', fontSize: '10px', marginTop: '4px', letterSpacing: '0.04em' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── Quote ── */}
          <div style={{ marginTop: 'auto', paddingTop: '28px', position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{
                width: '3px', flexShrink: 0,
                background: 'linear-gradient(to bottom, #F7D000, transparent)',
                borderRadius: '2px', alignSelf: 'stretch',
              }} />
              <div>
                <p style={{ fontFamily: '"Noto Serif", Georgia, serif', fontStyle: 'italic', fontSize: '13.5px', color: '#94A3B8', lineHeight: 1.7, margin: 0 }}>
                  "Pendidikan Berkualiti Melalui Transformasi Digital"
                </p>
                <cite style={{ display: 'block', marginTop: '8px', fontSize: '11px', color: '#475569', fontStyle: 'normal', letterSpacing: '0.02em' }}>
                  — Pelan Digital KPM 2023–2027
                </cite>
              </div>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════
            RIGHT PANEL — Login Form
        ════════════════════════════════════════════════════════ */}
        <div className="lp-right">

          {/* Language toggle */}
          <div style={{ position: 'absolute', top: '24px', right: '28px', display: 'flex', alignItems: 'center', background: '#F1F5F9', borderRadius: '8px', padding: '3px', gap: '2px' }}>
            {(['ms', 'en'] as const).map(l => {
              const active = lang === l;
              return (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  aria-pressed={active}
                  className="lang-btn"
                  style={{
                    color:      active ? '#0F172A' : '#94A3B8',
                    background: active ? '#FFFFFF' : 'transparent',
                    boxShadow:  active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  {l === 'ms' ? 'BM' : 'EN'}
                </button>
              );
            })}
          </div>

          <div style={{ width: '100%', maxWidth: '400px', position: 'relative', zIndex: 1 }}>

            {/* Welcome + heading */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: 'rgba(247,208,0,0.10)', border: '1px solid rgba(247,208,0,0.25)',
                borderRadius: '999px', padding: '4px 12px',
                fontSize: '11px', fontWeight: 700, color: '#92700A',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                marginBottom: '16px',
              }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#F7D000', animation: 'lp-pulse 2s ease-in-out infinite' }} />
                {E ? 'Secure Portal · SERPS' : 'Portal Selamat · SERPS'}
              </div>
              <h1 style={{
                fontFamily: '"Noto Serif", Georgia, serif',
                fontSize: '34px', fontWeight: 700,
                color: '#0F172A', lineHeight: 1.15,
                marginBottom: '10px', letterSpacing: '-0.02em',
              }}>
                {E ? 'Sign In' : 'Log Masuk'}
              </h1>
              <p style={{ fontSize: '14px', color: '#64748B', lineHeight: 1.55 }}>
                {E ? 'Enter your credentials to access the school management system.' : 'Masukkan butiran anda untuk mengakses sistem pengurusan sekolah.'}
              </p>
            </div>

            {/* Error */}
            {error && (
              <div role="alert" style={{
                background: '#FEF2F2', border: '1px solid #FECACA',
                borderLeft: '4px solid #EF4444', borderRadius: '10px',
                padding: '12px 14px', marginBottom: '20px',
                display: 'flex', alignItems: 'flex-start', gap: '10px',
              }}>
                <span style={{ color: '#EF4444', fontSize: '15px', flexShrink: 0, marginTop: '1px' }}>✕</span>
                <p style={{ margin: 0, fontSize: '13px', color: '#991B1B', lineHeight: 1.5 }}>{error}</p>
                <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: '18px', padding: 0, marginLeft: 'auto', lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Email field */}
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="lp-email" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '7px' }}>
                  {E ? 'Email' : 'Emel'}
                  <span style={{ color: '#EF4444', marginLeft: '3px' }}>*</span>
                </label>
                <div className="lp-field">
                  <span className="lp-input-icon">✉</span>
                  <input
                    id="lp-email" type="email"
                    className={`lp-input${error ? ' err' : ''}${selectedEmail && email === selectedEmail ? ' pre-filled' : ''}`}
                    placeholder={E ? 'name@school.edu.bn' : 'nama@sekolah.edu.bn'}
                    value={email}
                    onChange={e => { setEmail(e.target.value); setSelectedEmail(undefined); setError(null); }}
                    disabled={loading} autoComplete="username" required
                  />
                </div>
              </div>

              {/* Password field */}
              <div style={{ marginBottom: '24px' }}>
                <label htmlFor="lp-pw" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '7px' }}>
                  {E ? 'Password' : 'Kata Laluan'}
                  <span style={{ color: '#EF4444', marginLeft: '3px' }}>*</span>
                </label>
                <div className="lp-field">
                  <span className="lp-input-icon">🔒</span>
                  <input
                    id="lp-pw"
                    type={pwVisible ? 'text' : 'password'}
                    className={`lp-input${error ? ' err' : ''}${selectedEmail && email === selectedEmail ? ' pre-filled' : ''}`}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setSelectedEmail(undefined); setError(null); }}
                    disabled={loading} autoComplete="current-password" required
                  />
                  <button
                    type="button" className="lp-pw-toggle"
                    onClick={() => setPwVisible(v => !v)}
                    aria-label={pwVisible ? 'Hide password' : 'Show password'}
                    title={pwVisible ? (E?'Hide':'Sembunyikan') : (E?'Show':'Tunjukkan')}
                  >
                    {pwVisible ? '🙈' : '👁'}
                  </button>
                </div>
              </div>

              <button type="submit" className="lp-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span style={{ width: '18px', height: '18px', border: '2.5px solid rgba(15,23,42,0.2)', borderTopColor: '#0F172A', borderRadius: '50%', animation: 'lp-spin 0.7s linear infinite', display: 'inline-block', flexShrink: 0 }} />
                    {E ? 'Signing in…' : 'Sedang log masuk…'}
                  </>
                ) : (E ? '  Log Masuk / Sign In' : 'Log Masuk')}
              </button>
            </form>

            {/* Divider */}
            <div className="lp-divider">
              <div className="lp-divider-line" />
              <span className="lp-divider-txt">{E ? 'or sign in as' : 'atau log masuk sebagai'}</span>
              <div className="lp-divider-line" />
            </div>

            {/* Role pre-fill banner */}
            {selectedEmail && email === selectedEmail && (
              <div style={{ background:'#F0FDF4', border:'1px solid #86EFAC', borderRadius:'10px', padding:'10px 14px', marginBottom:'16px', display:'flex', alignItems:'center', gap:'10px' }}>
                <span style={{ fontSize:'16px' }}>✅</span>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:'13px', fontWeight:600, color:'#166534', margin:0 }}>
                    {E ? 'Credentials pre-filled' : 'Kelayakan telah diisi'}
                  </p>
                  <p style={{ fontSize:'11px', color:'#16A34A', margin:'2px 0 0' }}>
                    {E ? `Ready to sign in as ${selectedEmail}. Click "Log Masuk" to continue.`
                       : `Sedia log masuk sebagai ${selectedEmail}. Klik "Log Masuk" untuk teruskan.`}
                  </p>
                </div>
                <button onClick={() => { setEmail(''); setPassword(''); setSelectedEmail(undefined); }} style={{ background:'none', border:'none', cursor:'pointer', color:'#16A34A', fontSize:'16px', padding:'0', lineHeight:1, flexShrink:0 }}>×</button>
              </div>
            )}

            {/* Quick login */}
            <QuickLoginPanel onSelectRole={handleSelectRole} selectedEmail={selectedEmail} loading={loading} lang={lang} />

            {/* Footer */}
            <div style={{ textAlign: 'center', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #F1F5F9' }}>
              <p style={{ fontSize: '11px', color: '#94A3B8', lineHeight: 1.7 }}>
                © 2026 Kementerian Pendidikan Negara Brunei Darussalam
              </p>
              <p style={{ fontSize: '11px', color: '#CBD5E1', marginTop: '2px' }}>
                Sistem Pengurusan Rekod Sekolah (SERPS) v1.0 ·{' '}
                <span style={{ color: '#F7D000' }}>{lang === 'en' ? 'English' : 'Bahasa Melayu'}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
